import React from "react";
import { cn } from "@shadcn-in-nx/utils";
import { User, Bot, FileCode, Info, Wrench, Coins } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import ToolCallDetail from "./ToolCallDetail";

const MessageHeader = ({ role, createdAt }) => (
    <div className={cn(
        "px-3 py-2 border-b flex items-center justify-between",
        role === "human" ? "bg-blue-50" : "bg-purple-50"
    )}>
        <div className="flex items-center gap-2">
            <div className={cn(
                "p-1 rounded-full",
                role === "human" ? "bg-blue-100 text-blue-600" : "bg-purple-100 text-purple-600"
            )}>
                {role === "human" ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
            </div>
            <span className="font-medium text-sm">{role === "human" ? "User" : "Assistant"}</span>
        </div>
        <span className="text-[10px] text-gray-500">
            {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
        </span>
    </div>
);

const MessageContent = ({ content }) => {
    if (!content) return null;
    return (
        <div className="p-3 border-b">
            <div className="text-sm text-gray-700 whitespace-pre-wrap">{content}</div>
        </div>
    );
};

const TraceMetadata = ({ metadata }) => {
    if (!metadata) return null;
    return (
        <div className="p-3 bg-yellow-50 border-b">
            <div className="flex items-center gap-2 mb-1 text-yellow-700">
                <Info className="w-3 h-3" />
                <span className="text-xs font-medium">Trace Metadata</span>
            </div>
            <pre className="text-xs bg-yellow-100 p-2 rounded overflow-auto max-h-[800px]">
                {JSON.stringify(metadata, null, 2)}
            </pre>
        </div>
    );
};

const FileAttachments = ({ files }) => {
    if (!files?.length) return null;
    return (
        <div className="p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-1 text-gray-700">
                <FileCode className="w-3 h-3" />
                <span className="text-xs font-medium">Attachments</span>
            </div>
            <div className="flex flex-wrap gap-1">
                {files.map(({ file }) => (
                    <a
                        key={file.id}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-white border px-1.5 py-0.5 rounded hover:bg-gray-100"
                    >
                        {file.name}
                    </a>
                ))}
            </div>
        </div>
    );
};

const CreditInfo = ({ message, t }) => {
    if (!message.creditsUsed && !message.inputTokensUsed && !message.outputTokensUsed) return null;
    const creditColor = (message.creditsUsed || 0) >= 50
        ? "text-red-600" : (message.creditsUsed || 0) >= 25
        ? "text-yellow-600" : "text-green-600";
    return (
        <div className="p-3 bg-blue-50 border-b">
            <div className="flex items-center gap-2 mb-1 text-blue-700">
                <Coins className="w-3 h-3" />
                <span className="text-xs font-medium">{t("adminChats.messageCost")}</span>
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
                {message.creditsUsed != null && (
                    <span className={cn("font-semibold", creditColor)}>
                        {t("adminChats.creditsUsed")}: {message.creditsUsed.toFixed(4)}
                    </span>
                )}
                {message.inputTokensUsed != null && (
                    <span className="text-gray-600">{t("adminChats.inputTokens")}: {message.inputTokensUsed.toLocaleString()}</span>
                )}
                {message.outputTokensUsed != null && (
                    <span className="text-gray-600">{t("adminChats.outputTokens")}: {message.outputTokensUsed.toLocaleString()}</span>
                )}
                {message.llmModel && (
                    <span className="text-gray-500">{t("adminChats.model")}: {message.llmModel}</span>
                )}
            </div>
        </div>
    );
};

const ToolGroupSummary = ({ items, creditsUsed }) => {
    const toolCalls = items.filter(i => i.role === "tool_call");
    const successCount = items.filter(i =>
        i.role === "tool_response" &&
        i.functionResponse?.success !== false &&
        !i.functionResponse?.error
    ).length;
    const errorCount = toolCalls.length - successCount;
    const creditColor = (creditsUsed || 0) >= 50
        ? "text-red-600 bg-red-100" : (creditsUsed || 0) >= 25
        ? "text-yellow-600 bg-yellow-100" : "text-gray-600 bg-gray-100";

    return (
        <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border-b">
            <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-amber-100">
                    <Wrench className="w-3 h-3 text-amber-600" />
                </div>
                <span className="font-medium text-sm text-gray-800">
                    {toolCalls.length} Tool Call{toolCalls.length > 1 ? "s" : ""}
                </span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
                {successCount > 0 && (
                    <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        {successCount} ok
                    </span>
                )}
                {errorCount > 0 && (
                    <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                        {errorCount} failed
                    </span>
                )}
                {creditsUsed > 0 && (
                    <span className={cn("inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-medium", creditColor)}>
                        <Coins className="w-2.5 h-2.5" />{creditsUsed.toFixed(4)}
                    </span>
                )}
            </div>
        </div>
    );
};

export default function MessageDetailPanel({ item }) {
    const { t } = useTranslation();

    if (!item) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500">
                Select a message to view details
            </div>
        );
    }

    // Tool group view
    if (item.type === "tool_group") {
        const toolCalls = item.items.filter(i => i.role === "tool_call");

        return (
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <ToolGroupSummary items={item.items} creditsUsed={item.creditsUsed} />
                <div className="p-3 space-y-3 overflow-y-auto">
                    {toolCalls.map((toolCall, idx) => {
                        const toolResponse = item.items.find(
                            i => i.role === "tool_response" && i.toolCallId === toolCall.toolCallId
                        );
                        return (
                            <ToolCallDetail
                                key={toolCall.id || idx}
                                toolCall={toolCall}
                                toolResponse={toolResponse}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    // Human or Assistant message view
    return (
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <MessageHeader role={item.role} createdAt={item.createdAt} />
            <MessageContent content={item.content} />
            <CreditInfo message={item} t={t} />
            <TraceMetadata metadata={item.traceMetadata} />
            <FileAttachments files={item.files} />
        </div>
    );
}
